// ============================================================
//  src/pages/ProfilePage.jsx
// ============================================================

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";          // ← NEW
import { Card, Toast } from "../components/UI";
import LogoutModal from "../components/LogoutModal";
import { supabase } from "../utils/supabase";
import config from "../config";

// ──────────────────────────────────────────────────────────
// Field-level validators
// ──────────────────────────────────────────────────────────
const validators = {
  name(value) {
    if (!value.trim())                           return "Full name is required.";
    if (value.trim().length < 2)                 return "Name must be at least 2 characters.";
    if (value.trim().length > 50)                return "Name must be under 50 characters.";
    if (!/^[a-zA-Z\s'-]+$/.test(value.trim()))  return "Name can only contain letters, spaces, hyphens, or apostrophes.";
    return "";
  },
  phone(value) {
    if (!value.trim())                           return "Phone number is required.";
    const digits = value.replace(/\D/g, "");
    if (digits.length < 10)                      return "Phone number must be at least 10 digits.";
    if (digits.length > 15)                      return "Phone number must be under 15 digits.";
    if (!/^[+\d\s\-()]+$/.test(value.trim()))   return "Please enter a valid phone number.";
    return "";
  },
};

// ──────────────────────────────────────────────────────────
// Inline field error
// ──────────────────────────────────────────────────────────
function FieldError({ message }) {
  if (!message) return null;
  return (
    <div style={{
      fontSize: 11, color: "#dc2626", marginTop: -8,
      marginBottom: 10, paddingLeft: 4,
      display: "flex", alignItems: "center", gap: 4,
    }}>
      ⚠ {message}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Validated input
// ──────────────────────────────────────────────────────────
function ValidatedInput({ label, type = "text", placeholder, value, onChange, onBlur, error, touched, disabled, maxLength }) {
  const borderColor = disabled
    ? "#e5e7eb"
    : touched ? (error ? "#dc2626" : "#16a34a") : "#e5e7eb";

  const bgColor = disabled
    ? "#f3f4f6"
    : touched ? (error ? "#fef2f2" : "#f0fdf4") : "#f9fafb";

  return (
    <div style={{ marginBottom: error && touched ? 4 : 14 }}>
      <label style={{
        fontSize: 11, fontWeight: 700, color: "#888",
        letterSpacing: 0.5, textTransform: "uppercase",
        display: "block", marginBottom: 5,
      }}>
        {label}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        maxLength={maxLength}
        style={{
          width: "100%", padding: "11px 13px", fontSize: 14,
          border: `1.5px solid ${borderColor}`,
          borderRadius: 10, background: bgColor,
          outline: "none", boxSizing: "border-box",
          color: disabled ? "#aaa" : config.colors.text,
          cursor: disabled ? "not-allowed" : "text",
          transition: "border-color 0.2s, background 0.2s",
        }}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Read-only info row
// ──────────────────────────────────────────────────────────
function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div style={{ fontSize: 18, width: 26, paddingTop: 2 }}>{icon}</div>
      <div>
        <div style={{
          fontSize: 10, color: "#bbb", fontWeight: 800,
          textTransform: "uppercase", letterSpacing: 0.5,
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 14, fontWeight: 600, marginTop: 2,
          color: config.colors.text, wordBreak: "break-all",
        }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────────────────
export default function ProfilePage({ auth, setGlobalToast }) {   // ← removed setPage
  const navigate = useNavigate();                                   // ← NEW
  const { user, logout, refreshUser } = auth;

  const [editing, setEditing]         = useState(false);
  const [form, setForm]               = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [touched, setTouched]         = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState("");
  const [toast, setToast]             = useState(null);
  const [showLogout, setShowLogout]   = useState(false);
  const submitting                    = useRef(false);

  const userName = user?.name  || "";
  const initial  = userName.charAt(0)?.toUpperCase() || "U";

  function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  }

  function handleChange(key) {
    return (e) => {
      const value = e.target.value;
      setSaveError("");
      setForm((f) => ({ ...f, [key]: value }));
      if (touched[key]) {
        setFieldErrors((prev) => ({ ...prev, [key]: validators[key]?.(value) || "" }));
      }
    };
  }

  function handleBlur(key) {
    return () => {
      setTouched((prev) => ({ ...prev, [key]: true }));
      setFieldErrors((prev) => ({ ...prev, [key]: validators[key]?.(form[key]) || "" }));
    };
  }

  function validateAll() {
    const errs = {
      name:  validators.name(form.name),
      phone: validators.phone(form.phone),
    };
    setFieldErrors(errs);
    setTouched({ name: true, phone: true });
    return Object.values(errs).every((e) => !e);
  }

  async function handleSave() {
    if (submitting.current) return;
    if (!validateAll()) return;

    submitting.current = true;
    setSaving(true);
    setSaveError("");

    try {
      const { data, error } = await supabase
        .from("users")
        .update({ name: form.name.trim(), phone: form.phone.trim() })
        .eq("id", user.id)
        .select()
        .single();

      if (error) throw error;

      refreshUser({ name: data.name, phone: data.phone });
      setEditing(false);
      setTouched({});
      setFieldErrors({});
      setToast("Profile updated successfully!");

    } catch (err) {
      console.error("Profile update error:", err);
      setSaveError(err.message || "Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
      submitting.current = false;
    }
  }

  function handleCancel() {
    setEditing(false);
    setForm({ name: user?.name || "", phone: user?.phone || "" });
    setFieldErrors({});
    setTouched({});
    setSaveError("");
  }

  // Navigate FIRST before logout nulls out user
  async function handleLogout() {
    setShowLogout(false);
    setGlobalToast("You have been logged out successfully!");
    navigate("/");          // ← was setPage("home")
    await logout();
  }

  // Redirect to home if user logs out externally
  useEffect(() => {
    if (!user) { navigate("/"); }   // ← was setPage("home")
  }, [user]); // eslint-disable-line

  if (!user) return null;

  return (
    <div style={{ padding: "18px 13px 100px", maxWidth: 520, margin: "0 auto" }}>

      {/* ── Avatar / Header ── */}
      <div style={{
        background: `linear-gradient(135deg, ${config.colors.primary}, ${config.colors.primaryLight})`,
        borderRadius: 20, padding: "28px 22px",
        color: "#fff", textAlign: "center", marginBottom: 16,
      }}>
        <div style={{
          width: 72, height: 72,
          background: "rgba(255,255,255,0.25)",
          borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 34, margin: "0 auto 12px", fontWeight: 800,
        }}>
          {initial}
        </div>
        <div className="font-display" style={{ fontSize: 22, fontWeight: 800 }}>
          {userName || "User"}
        </div>
        <div style={{ opacity: 0.85, fontSize: 13, marginTop: 4 }}>
          {user?.email || "—"}
        </div>
        <div style={{
          marginTop: 10, display: "inline-block",
          background: "rgba(255,255,255,0.2)",
          borderRadius: 20, padding: "4px 14px",
          fontSize: 12, fontWeight: 700,
        }}>
          ✅ Verified Account
        </div>
      </div>

      {/* ── Account Details ── */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>👤 Account Details</div>
          {!editing && (
            <button
              className="btn-press"
              onClick={() => { setEditing(true); setSaveError(""); }}
              style={{
                background: config.colors.background,
                border: `1px solid ${config.colors.cardBorder}`,
                borderRadius: 20, padding: "5px 14px",
                fontSize: 12, fontWeight: 700,
                color: config.colors.primary, cursor: "pointer",
              }}
            >
              ✏️ Edit
            </button>
          )}
        </div>

        {editing ? (
          <>
            <ValidatedInput
              label="Full Name *" placeholder="Your full name"
              value={form.name} onChange={handleChange("name")}
              onBlur={handleBlur("name")} error={fieldErrors.name}
              touched={touched.name} maxLength={50}
            />
            <FieldError message={touched.name && fieldErrors.name} />

            <ValidatedInput
              label="Email (cannot be changed)" type="email"
              value={user?.email || ""} disabled
            />

            <ValidatedInput
              label="Phone Number *" type="tel" placeholder="+91 98765 43210"
              value={form.phone} onChange={handleChange("phone")}
              onBlur={handleBlur("phone")} error={fieldErrors.phone}
              touched={touched.phone} maxLength={15}
            />
            <FieldError message={touched.phone && fieldErrors.phone} />

            {saveError && (
              <div style={{
                color: "#dc2626", fontSize: 13, marginBottom: 12,
                background: "#fef2f2", padding: "10px 12px",
                borderRadius: 8, border: "1px solid #fecaca",
              }}>
                ⚠️ {saveError}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button
                className="btn-press" onClick={handleSave} disabled={saving}
                style={{
                  flex: 1, background: saving ? "#bbb" : config.colors.primary,
                  color: "#fff", border: "none", borderRadius: 12,
                  padding: "12px 0", fontWeight: 800, fontSize: 14,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "⏳ Saving..." : "💾 Save Changes"}
              </button>
              <button
                className="btn-press" onClick={handleCancel}
                style={{
                  flex: 1, background: "#fff", color: config.colors.primary,
                  border: `2px solid ${config.colors.primary}`,
                  borderRadius: 12, padding: "12px 0",
                  fontWeight: 700, fontSize: 14, cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <InfoRow icon="📧" label="Email"        value={user?.email      || "—"} />
            <InfoRow icon="👤" label="Name"         value={userName         || "—"} />
            <InfoRow icon="📱" label="Phone"        value={user?.phone      || "Not set"} />
            <InfoRow icon="📅" label="Member Since" value={fmtDate(user?.createdAt)} />
            <InfoRow icon="🕐" label="Last Login"   value={fmtDate(user?.lastLoginAt)} />
          </div>
        )}
      </Card>

      {/* ── Contact Us ── */}
      <Card>
        <button
          className="btn-press"
          onClick={() => navigate("/contact")}    // ← was setPage("contact")
          style={{
            width: "100%",
            background: config.colors.background,
            color: config.colors.primary,
            border: `1.5px solid ${config.colors.cardBorder}`,
            borderRadius: 14,
            padding: "16px 0",
            fontWeight: 800,
            fontSize: 15,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          📞 Contact Us
        </button>
      </Card>

      {/* ── Sign Out ── */}
      <Card>
        <button
          className="btn-press"
          onClick={() => setShowLogout(true)}
          style={{
            width: "100%",
            background: "#e74c3c",
            color: "#fff",
            border: "none",
            borderRadius: 14,
            padding: "16px 0",
            fontWeight: 900,
            fontSize: 17,
            cursor: "pointer",
            letterSpacing: 0.4,
            boxShadow: "0 4px 14px rgba(231,76,60,0.35)",
          }}
        >
          🚪 Sign Out
        </button>
      </Card>

      {showLogout && (
        <LogoutModal
          user={user}
          onConfirm={handleLogout}
          onCancel={() => setShowLogout(false)}
        />
      )}

      {/* Local toast for profile save success */}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
